import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table } from '@heroui/react';
import { countParam } from '@/pages/catalogImport';
import type { BarcodeIssue } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { BarcodeIssueFixCell } from './BarcodeIssueFixCell';
import { problemCopyKeyOf, type BarcodeIssueChoice } from './barcodeIssuesMeta';
import type { UseBarcodeIssuesResult } from './useBarcodeIssues';

const COPY = 'products.barcodeIssues';

type BarcodeIssuesRowProps = Pick<
    UseBarcodeIssuesResult,
    'onFixChange' | 'onTypedChange' | 'onUseAnyway'
> & {
    issue: BarcodeIssue;
    rowKey: string;
    choice: BarcodeIssueChoice;
    /** The refusal code the last fix call put on this row, if any. */
    errorCode: string | null;
    isDisabled: boolean;
};

/**
 * One listed barcode: its checkbox, product, variant, SKU, the barcode now,
 * main or extra, the problem, its fix and the stock it counts.
 */
export const BarcodeIssuesRow: React.FC<BarcodeIssuesRowProps> = ({
    issue,
    rowKey,
    choice,
    errorCode,
    isDisabled,
    onFixChange,
    onTypedChange,
    onUseAnyway,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Table.Row
            id={rowKey}
            {...getNodeTestIdProp(`barcode-issues-row-${rowKey}`)}
        >
            <Table.Cell className="pe-0">
                {/* The table names the checkbox after its row header too:
                    "Select <product>". */}
                <Checkbox
                    aria-label={t(`${COPY}.columns.select`)}
                    slot="selection"
                    variant="secondary"
                    {...getNodeTestIdProp(`barcode-issues-select-${rowKey}`)}
                >
                    <Checkbox.Content>
                        <Checkbox.Control>
                            <Checkbox.Indicator />
                        </Checkbox.Control>
                    </Checkbox.Content>
                </Checkbox>
            </Table.Cell>
            <Table.Cell className="font-medium">{issue.productName}</Table.Cell>
            <Table.Cell>{issue.variantLabel ?? ''}</Table.Cell>
            <Table.Cell className="font-mono">{issue.sku ?? ''}</Table.Cell>
            <Table.Cell className="font-mono break-all">
                {issue.barcode}
            </Table.Cell>
            <Table.Cell>{t(`${COPY}.role.${issue.role}`)}</Table.Cell>
            <Table.Cell className="min-w-48 whitespace-normal">
                {t(`${COPY}.problem.${problemCopyKeyOf(issue)}`)}
            </Table.Cell>
            <Table.Cell>
                <BarcodeIssueFixCell
                    choice={choice}
                    error={
                        errorCode === null
                            ? null
                            : t(`${COPY}.refused.${errorCode}`)
                    }
                    isDisabled={isDisabled}
                    issue={issue}
                    onFixChange={onFixChange}
                    onTypedChange={onTypedChange}
                    onUseAnyway={onUseAnyway}
                    rowKey={rowKey}
                />
            </Table.Cell>
            <Table.Cell className="text-end tabular-nums">
                {countParam(issue.stock)}
            </Table.Cell>
        </Table.Row>
    );
};
